'use strict';

import { PIPELINE_STATUS_MOCK_SCENARIO, USE_PIPELINE_STATUS_API_MOCKS } from '@/config/env.config';
import {
	PIPELINE_STATUS_FLOW_SCENARIO_KEYS,
	pipelineStatusFlowScenarioFromProjectPipeline,
	requirePipelineStatusFlowScenarioKey,
	SINGLE_CORE_PROJECT_FLOW_SCENARIO,
	SINGLE_V7_PROJECT_FLOW_SCENARIO,
} from '../pipelineStatusFlowScenarios';
import { PipelineStatusWorkflowNames } from '../pipelineStatusTypes';

export { SINGLE_CORE_PROJECT_FLOW_SCENARIO, SINGLE_V7_PROJECT_FLOW_SCENARIO };

/**
 * Literal API-shaped fixtures for `GET …/admin/pipeline-status/projects/{projectId}/facts`.
 *
 * - `SINGLE_V7_PROJECT_FLOW_MOCK_ROWS` — fact DTOs as returned by PortalApi (`PipelineStatusFactDto`).
 * - `SINGLE_V7_PROJECT_FLOW_ITERATION_MANIFESTS` — manifest DTOs (`PipelineStatusIterationManifestDto`).
 *
 * These arrays are not graph logic: they only mirror what the API would return. Which graph nodes are
 * manifest-aware is defined in `pipelineStatusGraphDefinition.js` (`iterationManifestMatch`).
 *
 * Use {@link getMockPipelineStatusSnapshotForScenario} for `{ facts, iterationManifests }`.
 */

export const V7_PROJECT_ID = '3fa85f64-5717-4562-b3fc-2c963f66afa6';

export const V7_SAMPLE_SET_IDS = Object.freeze([
	'8b2d9c10-1111-4222-9333-444444444444',
	'8b2d9c10-1111-4222-9333-555555555555',
]);

/** @deprecated Use {@link V7_SAMPLE_SET_IDS} */
export const V7_SAMPLE_SET_ID = V7_SAMPLE_SET_IDS[0];

export const V7_CORRELATION_ID = 'b1000001-0001-4001-8001-000000000001';

/**
 * Literal fact rows for V7 / Project / Publishing (append-only; SS1 complete, SS2 in progress).
 * `iterationDimension` / `iterationValue` appear only where the API would return them on a fact row.
 */
const SINGLE_V7_PROJECT_FLOW_MOCK_ROWS = [
	{
		id: 'a0000001-0000-4000-8000-000000000001',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'V7ProjectPoller',
		status: 'Started',
		occurredAt: '2026-05-07T09:40:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000002',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'V7ProjectPoller',
		status: 'Completed',
		occurredAt: '2026-05-07T09:50:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000003',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'V7ProjectAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T09:59:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000004',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'V7ProjectAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:00:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000005',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'V7SampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:01:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000006',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'V7SampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:02:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000007',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'V7SampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:02:30.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-555555555555',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000008',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:03:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000009',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:04:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000010',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:04:30.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PortalApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-555555555555',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000011',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:05:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'BioVizService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000012',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:06:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'BioVizService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000013',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:06:15.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'BioVizService',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-555555555555',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000014',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:06:30.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PathwayExplorerApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000015',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:07:00.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PathwayExplorerApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-444444444444',
	},
	{
		id: 'a0000001-0000-4000-8000-000000000016',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'V7',
		stage: 'ProductSampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:07:15.000Z',
		correlationId: V7_CORRELATION_ID,
		service: 'PathwayExplorerApi',
		serviceVersion: '2.3.4',
		iterationDimension: 'SampleSetId',
		iterationValue: '8b2d9c10-1111-4222-9333-555555555555',
	},
];

/** One manifest for the V7ProjectPoller sample-set fan-out (not one manifest per graph node). */
const SINGLE_V7_PROJECT_FLOW_ITERATION_MANIFESTS = [
	{
		id: 'm0000001-0000-4000-8000-000000000001',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: V7_PROJECT_ID,
		pipeline: 'V7',
		parentStage: 'V7ProjectPoller',
		parentService: 'V7ProductService',
		correlationId: V7_CORRELATION_ID,
		iterationDimension: 'SampleSetId',
		iterationValues: [
			'8b2d9c10-1111-4222-9333-444444444444',
			'8b2d9c10-1111-4222-9333-555555555555',
		],
		createdAtUtc: '2026-05-07T09:40:30.000Z',
		sourceService: 'V7ProductService',
	},
];

const CORE_PROJECT_ID = '3fa85f64-5717-4562-b3fc-2c963f66afa7';
const CORE_CORRELATION_ID = 'b1000001-0001-4001-8001-000000000002';

const SINGLE_CORE_PROJECT_FLOW_MOCK_ROWS = [
	{
		id: 'a0000002-0000-4000-8000-000000000000',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: CORE_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'Core',
		stage: 'V7SampleSetAvailable',
		status: 'Started',
		occurredAt: '2026-05-07T10:00:00.000Z',
		correlationId: CORE_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
	},
	{
		id: 'a0000002-0000-4000-8000-000000000001',
		scope: 'Project',
		workflow: PipelineStatusWorkflowNames.Publishing,
		subjectId: CORE_PROJECT_ID,
		parentWorkflow: null,
		parentSubjectId: null,
		pipeline: 'Core',
		stage: 'V7SampleSetAvailable',
		status: 'Completed',
		occurredAt: '2026-05-07T10:00:00.000Z',
		correlationId: CORE_CORRELATION_ID,
		service: 'V7ProductService',
		serviceVersion: '2.3.4',
	},
];

const MOCK_FACT_ROWS_BY_SCENARIO = {
	[SINGLE_V7_PROJECT_FLOW_SCENARIO]: SINGLE_V7_PROJECT_FLOW_MOCK_ROWS,
	[SINGLE_CORE_PROJECT_FLOW_SCENARIO]: SINGLE_CORE_PROJECT_FLOW_MOCK_ROWS,
};

const MOCK_ITERATION_MANIFESTS_BY_SCENARIO = {
	[SINGLE_V7_PROJECT_FLOW_SCENARIO]: SINGLE_V7_PROJECT_FLOW_ITERATION_MANIFESTS,
	[SINGLE_CORE_PROJECT_FLOW_SCENARIO]: [],
};

export const PIPELINE_STATUS_MOCK_SCENARIO_KEYS = PIPELINE_STATUS_FLOW_SCENARIO_KEYS;

/**
 * Shallow-clones snapshot rows so tests can mutate without affecting fixtures.
 * @param {Record<string, unknown>[]} rows
 * @returns {Record<string, unknown>[]}
 */
function cloneRows(rows) {
	return rows.map((r) => {
		const row = { ...r };
		if (Array.isArray(row.iterationValues)) {
			row.iterationValues = [...row.iterationValues];
		}
		return row;
	});
}

/**
 * Resolves which mock fixture scenario to serve when pipeline status API mocks are enabled (`USE_PIPELINE_STATUS_API_MOCKS` in `env.config.js`).
 * If `PIPELINE_STATUS_MOCK_SCENARIO` is set, it must be a known flow key. When unset, uses `projectPipeline` like live mode ({@link pipelineStatusFlowScenarioFromProjectPipeline}; default V7).
 *
 * @param {unknown} [projectPipeline] Portal `ProjectDescription.pipeline` when the env override is unset.
 * @returns {string}
 */
export function requirePipelineStatusMockScenarioFromEnv(projectPipeline) {
	if (!USE_PIPELINE_STATUS_API_MOCKS) {
		throw new Error(
			'[pipelineStatus] requirePipelineStatusMockScenarioFromEnv is only valid when pipeline status API mocks are enabled',
		);
	}
	const override = PIPELINE_STATUS_MOCK_SCENARIO;
	if (override != null && String(override).trim() !== '') {
		return requirePipelineStatusFlowScenarioKey(override);
	}
	return pipelineStatusFlowScenarioFromProjectPipeline(projectPipeline);
}

/**
 * Snapshot-shaped mock for a flow scenario (`facts` + `iterationManifests`), cloned for safe mutation in tests.
 *
 * @param {unknown} scenarioKey
 * @returns {{ facts: Record<string, unknown>[], iterationManifests: Record<string, unknown>[] }}
 */
export function getMockPipelineStatusSnapshotForScenario(scenarioKey) {
	const key = requirePipelineStatusFlowScenarioKey(scenarioKey);
	const facts = MOCK_FACT_ROWS_BY_SCENARIO[key];
	if (!facts) {
		throw new Error(`[pipelineStatus] Unknown pipeline status flow scenario: ${JSON.stringify(key)}`);
	}
	const manifests = MOCK_ITERATION_MANIFESTS_BY_SCENARIO[key] ?? [];
	return {
		facts: cloneRows(facts),
		iterationManifests: cloneRows(manifests),
	};
}

/** @param {unknown} scenarioKey @returns {Record<string, unknown>[]} */
export function getMockPipelineStatusFactRowsForScenario(scenarioKey) {
	return getMockPipelineStatusSnapshotForScenario(scenarioKey).facts;
}

/** @param {unknown} scenarioKey @returns {Record<string, unknown>[]} */
export function getMockPipelineStatusIterationManifestsForScenario(scenarioKey) {
	return getMockPipelineStatusSnapshotForScenario(scenarioKey).iterationManifests;
}
